import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import Select from '../components/common/Select';
import AuthLayout from '../components/layout/AuthLayout';

export default function Signup() {
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', password: '',
    chambers_name: '', tenant_type: 'solo' as 'solo' | 'chambers',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nav = useNavigate();
  const { loginWithToken } = useAuth();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await authApi.signup(form);
      await loginWithToken(r.token, r.tenant.id);
      nav('/onboarding', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      heading="Start your 30-day trial"
      subheading="Litigate is a practice-management platform built for South African advocates — court rules, time, files, deadlines, all wired to your matters."
    >
      <form className="card stack" onSubmit={onSubmit} style={{ gap: 14 }}>
        <div className="row" style={{ gap: 10 }}>
          <div className="grow">
            <label htmlFor="su-first">First name</label>
            <input
              id="su-first"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              required
              autoComplete="given-name"
            />
          </div>
          <div className="grow">
            <label htmlFor="su-last">Last name</label>
            <input
              id="su-last"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              required
              autoComplete="family-name"
            />
          </div>
        </div>
        <div>
          <label htmlFor="su-email">Email</label>
          <input
            id="su-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            autoComplete="email"
            placeholder="you@chambers.co.za"
          />
        </div>
        <div>
          <label htmlFor="su-password">Password</label>
          <input
            id="su-password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="At least 8 characters"
          />
        </div>
        <div>
          <label htmlFor="su-chambers">Chambers / practice name</label>
          <input
            id="su-chambers"
            value={form.chambers_name}
            onChange={(e) => setForm({ ...form, chambers_name: e.target.value })}
            required
            placeholder='e.g. "Group 621" or "Smith Advocates"'
          />
        </div>
        <div>
          <label>Practice type</label>
          <Select
            value={form.tenant_type}
            onChange={(v) => setForm({ ...form, tenant_type: v as 'solo' | 'chambers' })}
            options={[
              { value: 'solo',     label: 'Solo advocate' },
              { value: 'chambers', label: 'Chambers / group' },
            ]}
          />
        </div>
        {error && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius)',
              background: 'var(--color-danger-soft)',
              border: '1px solid rgba(229, 85, 60, 0.35)',
              color: 'var(--color-danger)',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <button type="submit" className="primary" disabled={submitting} style={{ marginTop: 4 }}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      {/* Disclaimer sits OUTSIDE the form so it isn't expanded by the form's
          flex gap. Small muted text, no negative margin tricks. */}
      <p
        style={{
          marginTop: 10,
          fontSize: 11,
          lineHeight: 1.5,
          color: 'var(--color-text-muted)',
        }}
      >
        By creating an account you agree to the Litigate terms of service. No card required for the 30-day trial.
      </p>

      <p style={{ marginTop: 14, fontSize: 13, color: 'var(--color-text-muted)' }}>
        Already have an account?{' '}
        <Link to="/login" style={{ color: 'var(--color-accent)', fontWeight: 500 }}>
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
